import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PostingAccountMappingSchema,
  type AccountDto,
  type PostingAccountMappingDto,
} from '@taqon/contracts';
import { getPage, patchJson, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { AccountPicker } from '../finance/lookups';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';

const PATH = '/api/v1/ledger/posting-account-mappings';

/**
 * Posting Account Mappings (ERD §5.14, MD-19; FRD-FIN-072): which account a posting purpose uses
 * where nothing more specific gives one. The existing API offers list, create and status change;
 * the key and the account are never amended in place. No selection criterion exists for choosing
 * between alternative accounts for one purpose — that is open Finance decision CLR-33 — so this
 * screen offers none.
 */
export function PostingMappingsPage() {
  const session = useSession();
  const queryClient = useQueryClient();
  const canCreate = hasCapability(session.data, 'ledger.posting_account_mapping:create');
  const canChange = hasCapability(session.data, 'ledger.posting_account_mapping:change');
  const [open, setOpen] = useState(false);
  const [transactionType, setTransactionType] = useState('');
  const [postingPurpose, setPostingPurpose] = useState('');
  const [account, setAccount] = useState<AccountDto | null>(null);

  const mappings = useQuery({
    queryKey: ['posting-account-mappings'],
    queryFn: () => getPage(`${PATH}?pageSize=200`, PostingAccountMappingSchema),
  });

  const create = useMutation({
    mutationFn: () =>
      postJson(
        PATH,
        {
          transactionType: transactionType.trim(),
          postingPurpose: postingPurpose.trim(),
          accountId: account?.id ?? '',
        },
        PostingAccountMappingSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async () => {
      setOpen(false);
      setTransactionType('');
      setPostingPurpose('');
      setAccount(null);
      await queryClient.invalidateQueries({ queryKey: ['posting-account-mappings'] });
    },
  });

  const toggle = useMutation({
    mutationFn: (mapping: PostingAccountMappingDto) =>
      patchJson(
        `${PATH}/${mapping.id}`,
        { status: mapping.status === 'active' ? 'inactive' : 'active' },
        PostingAccountMappingSchema,
        session.data?.csrfToken ?? '',
        mapping.version,
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['posting-account-mappings'] });
    },
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Posting mappings"
        description="The account each posting purpose uses when a transaction is posted."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                create.reset();
                setOpen(true);
              }}
            >
              New mapping
            </Button>
          ) : undefined
        }
      />
      {mappings.isError && <ErrorState message={mappings.error.message} />}
      {toggle.isError && <ErrorState message={toggle.error.message} />}
      <Section>
        {mappings.isPending && <LoadingState label="Loading posting mappings" />}
        {mappings.data !== undefined && mappings.data.items.length === 0 && (
          <EmptyState
            title="No posting mappings yet"
            detail="Mappings are configured after the Chart of Accounts has been imported."
          />
        )}
        {mappings.data !== undefined && mappings.data.items.length > 0 && (
          <Table size="small" aria-label="Posting mappings">
            <TableHead>
              <TableRow>
                <TableCell>Transaction type</TableCell>
                <TableCell>Posting purpose</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Status</TableCell>
                {canChange && <TableCell align="right">Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {mappings.data.items.map((mapping) => (
                <TableRow key={mapping.id}>
                  <TableCell>{humanize(mapping.transactionType)}</TableCell>
                  <TableCell>{humanize(mapping.postingPurpose)}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{mapping.accountCode}</TableCell>
                  <TableCell>
                    <StatusChip status={mapping.status} />
                  </TableCell>
                  {canChange && (
                    <TableCell align="right">
                      <Button
                        size="small"
                        disabled={toggle.isPending}
                        onClick={() => {
                          toggle.mutate(mapping);
                        }}
                      >
                        {mapping.status === 'active' ? 'Deactivate' : 'Activate'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <FormDialog
        open={open}
        title="New posting mapping"
        submitLabel="Create"
        canSubmit={
          transactionType.trim() !== '' && postingPurpose.trim() !== '' && account !== null
        }
        submitting={create.isPending}
        error={create.isError ? create.error.message : null}
        onClose={() => {
          setOpen(false);
        }}
        onSubmit={() => {
          create.mutate();
        }}
      >
        <TextField
          label="Transaction type"
          required
          autoFocus
          value={transactionType}
          onChange={(e) => {
            setTransactionType(e.target.value);
          }}
          helperText="The transaction this applies to, for example supplier_invoice."
        />
        <TextField
          label="Posting purpose"
          required
          value={postingPurpose}
          onChange={(e) => {
            setPostingPurpose(e.target.value);
          }}
          helperText="The role the account plays, for example payable."
        />
        {/* Searched on the server, so every active posting account can be chosen (FRD-FIN-072). */}
        <AccountPicker value={account} onChange={setAccount} />
      </FormDialog>
    </Stack>
  );
}

/** `supplier_invoice` → `Supplier invoice`: presentation only, the stored key is unchanged. */
function humanize(key: string): string {
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
